import { BranchTargetKind } from '@/contracts/enums/branching';
import { BranchRuleTarget } from '@/contracts/models/survey-branching';
import { EditorQuestion } from '@/contracts/models/survey-editor';
import { getBranchChildLabel } from '@/features/survey-builder/utils/branchingService';
import { useTranslation } from '@/hooks/useTranslation';
import { Dropdown } from 'primereact/dropdown';
import React from 'react';

const NEXT_OPTION_VALUE = '__next__';

export interface BranchTargetDropdownProps {
  value?: BranchRuleTarget;
  onChange: (target: BranchRuleTarget) => void;
  branchChildren: EditorQuestion[];
  parentNumber: number;
  isLastQuestion: boolean;
  disabled?: boolean;
  ariaLabel: string;
}

export const BranchTargetDropdown: React.FC<BranchTargetDropdownProps> = ({
  value,
  onChange,
  branchChildren,
  parentNumber,
  isLastQuestion,
  disabled = false,
  ariaLabel,
}) => {
  const { t } = useTranslation();

  const options = [
    ...branchChildren.map((child, index) => ({
      label: t('editor.branching.goToLabel', {
        label: getBranchChildLabel(parentNumber, index),
      }),
      value: child.id,
    })),
    {
      label: isLastQuestion
        ? t('editor.branching.endOfSurvey')
        : t('editor.branching.goToLabel', { label: `${parentNumber + 1}` }),
      value: NEXT_OPTION_VALUE,
    },
  ];

  const selectedValue =
    value?.kind === BranchTargetKind.BranchChild && value.childId
      ? value.childId
      : NEXT_OPTION_VALUE;

  const handleChange = (newValue: string) => {
    onChange(
      newValue === NEXT_OPTION_VALUE
        ? { kind: BranchTargetKind.NextQuestion }
        : { kind: BranchTargetKind.BranchChild, childId: newValue }
    );
  };

  return (
    <Dropdown
      value={selectedValue}
      options={options}
      onChange={e => handleChange(e.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      className='w-28 flex-shrink-0'
      pt={{ input: { className: 'py-2 px-2 text-sm' } }}
    />
  );
};
